import React from 'react';
import PropTypes from 'prop-types';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import { formatPriceLabel } from 'common/lib/helpers/formatPrice';
import { AD_TYPE_ETS, AD_TYPE_PTSF_UNIT, AD_TYPE_PTS_UNIT } from 'common/lib/helpers/constants';
import { isProjectAd } from 'common/lib/helpers/adTypeHelpers';
import { getUnits } from '../unitSelector/unitFilter';
import { getFormattedPtsPrice } from './getFormattedPtsPrice';
import CollapsableArea from '../collapsableArea';

const AdFinancing = ({ data }) => {
    const { isMobile } = useDeviceSize();
    const {
        finnkode,
        adType,
        priceSuggestion,
        collectiveDebt,
        salesCostSum,
        totalPrice,
        adStatus,
        sharedCostAmount,
        sharedCostAmountAfterDeductionFreePeriod,
        collectiveAssetsAmount,
        estimatedValue,
        loanValueAmount,
        loanFare,
        taxValue,
        communityTax,
    } = data;
    const { units } = getUnits();

    const formattedPrice = [AD_TYPE_ETS, AD_TYPE_PTS_UNIT, AD_TYPE_PTSF_UNIT].includes(adType)
        ? formatPriceLabel(priceSuggestion)
        : getFormattedPtsPrice('priceSuggestion', units, adStatus, isMobile);

    const formattedTotalPrice = [AD_TYPE_ETS, AD_TYPE_PTS_UNIT, AD_TYPE_PTSF_UNIT].includes(adType)
        ? formatPriceLabel(totalPrice)
        : getFormattedPtsPrice('totalPrice', units, adStatus, isMobile);

    const priceInfo = (title, value, noFormat) => {
        if (value && value !== 0) {
            return (
                <div>
                    <p>{title}</p>
                    <p className="font-bold mb-0">{noFormat ? value : formatPriceLabel(value)}</p>
                </div>
            );
        }
        return '';
    };

    return (
        <div className="border-b-2 border-bluegray-200 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                {formattedPrice !== null && typeof formattedPrice === 'object' && isMobile ? (
                    <div>
                        <p>Pris fra</p>
                        <p className="text-28 font-bold">{formattedPrice.minPrice}</p>
                        <p>Pris til</p>
                        <p className="text-28 font-bold">{formattedPrice.maxPrice}</p>
                    </div>
                ) : (
                    <div className="col-span-1 md:col-span-2">
                        <p>Pris</p>
                        <p className="text-28 font-bold">{formattedPrice}</p>
                    </div>
                )}

                {priceInfo('Totalpris', formattedTotalPrice, true)}
                {priceInfo('Omkostninger', salesCostSum)}
                {priceInfo('Fellesgjeld', collectiveDebt)}
                {priceInfo('Felleskostnader per mnd', sharedCostAmount)}
            </div>

            {/* Only shows up in DEV and PROD */}
            {!isProjectAd(adType) && (
                <div className="-mx-16 mt-16">
                    <bfft-links finncode={finnkode} layout="noColumnsImproved" />
                </div>
            )}

            {(sharedCostAmountAfterDeductionFreePeriod ||
                collectiveAssetsAmount ||
                estimatedValue ||
                loanValueAmount ||
                loanFare ||
                taxValue ||
                communityTax) && (
                <CollapsableArea showMoreText="Se flere detaljer" showLessText="Se færre detaljer">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 my-16">
                        {priceInfo('Felleskostnader etter avdragsfriperiode', sharedCostAmountAfterDeductionFreePeriod)}
                        {priceInfo('Andel fellesformue', collectiveAssetsAmount)}
                        {priceInfo('Verditakst', estimatedValue)}
                        {priceInfo('Låneverdi', loanValueAmount)}
                        {priceInfo('Lånetakst', loanFare)}
                        {priceInfo('Ligningsverdi', taxValue)}
                        {priceInfo('Kommunale avgifter per år', communityTax)}
                    </div>
                </CollapsableArea>
            )}
        </div>
    );
};

AdFinancing.propTypes = {
    data: PropTypes.shape({
        units: PropTypes.array,
        finnkode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        adType: PropTypes.string,
        priceSuggestion: PropTypes.number,
        collectiveDebt: PropTypes.number,
        salesCostSum: PropTypes.number,
        sharedCostAmount: PropTypes.number,
        sharedCostAmountAfterDeductionFreePeriod: PropTypes.number,
        collectiveAssetsAmount: PropTypes.number,
        estimatedValue: PropTypes.number,
        loanValueAmount: PropTypes.number,
        loanFare: PropTypes.number,
        taxValue: PropTypes.number,
        communityTax: PropTypes.number,
        totalPrice: PropTypes.number,
        adStatus: PropTypes.string,
    }).isRequired,
};

export default AdFinancing;
